import { useMemo } from "react";

import Dropdown from "@/components/ui/dropdown";
import { DropdownDataItem } from "@/components/ui/dropdown/types";
import {
  AttributeOption,
  DatasetFilter,
  EFilterOptionType,
  FilterOption
} from "@/types/dataset";

import DateFilter from "./DateFilter";
import DateTimeFilter from "./DateTimeFilter";
import MinMaxFilter from "./MinMaxFilter";
import MultipleSelectionFilter from "./MultipleSelectionFilter";
import TimeFilter from "./TimeFilter";
import { DateRange, DateTimeRange, MinMaxRange, TimeRange } from "./types";

type AttributeFilterProps = {
  savedFilters?: DatasetFilter[];
  isEditMode?: boolean;
  getAvailableFilterOptions: () => DropdownDataItem<AttributeOption>;
  handleSelectFilter: (option: AttributeOption | null) => void;
  selectAttributeItem?: AttributeOption | null;
  defaultSelectedValue?: AttributeOption | null;
  attributeValues?: FilterOption[];
  searchTerm?: string;
  selectedOptions?: string[];
  isGettingAttributeValues?: boolean;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onChange: (
    data:
      | FilterOption[]
      | DateTimeRange
      | DateRange
      | TimeRange
      | MinMaxRange
      | string
  ) => void;
};

export const AttributeFilter = ({
  savedFilters = [],
  isEditMode = false,
  getAvailableFilterOptions,
  handleSelectFilter,
  selectAttributeItem,
  defaultSelectedValue,
  attributeValues,
  searchTerm = "",
  selectedOptions = [],
  onSearchChange,
  isGettingAttributeValues = false,
  onChange
}: AttributeFilterProps) => {
  const currentSelectedFilter = useMemo(
    () =>
      savedFilters.find(
        (filter) => filter.filterId === selectAttributeItem?.id
      ),
    [savedFilters, selectAttributeItem]
  );

  // Determine filter types
  const [
    isMultipleSelection,
    isDateTimeFilter,
    isDateFilter,
    isTimeFilter,
    isMinMaxFilter
  ] = useMemo(
    () => [
      selectAttributeItem?.type === EFilterOptionType.MULTIPLE,
      selectAttributeItem?.type === EFilterOptionType.DATE_TIME,
      selectAttributeItem?.type === EFilterOptionType.DATE,
      selectAttributeItem?.type === EFilterOptionType.TIME,
      selectAttributeItem?.type === EFilterOptionType.MIN_MAX
    ],
    [selectAttributeItem]
  );
  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="space-y-3 flex-none mt-3">
        <Dropdown
          name="Attribute Filter"
          placeholder="Attribute Filter"
          data={getAvailableFilterOptions()}
          onSelect={handleSelectFilter}
          value={selectAttributeItem}
          defaultValue={defaultSelectedValue}
        />
      </div>
      {isMultipleSelection && (
        <MultipleSelectionFilter
          isEdit={isEditMode}
          isLoading={isGettingAttributeValues}
          options={attributeValues ?? []}
          selectedOptions={selectedOptions}
          searchTerm={searchTerm}
          onSearchChange={onSearchChange}
          onSelectOption={onChange}
          onSelectAll={onChange}
        />
      )}
      {isDateTimeFilter && (
        <DateTimeFilter
          onSave={onChange}
          defaultValue={currentSelectedFilter?.dateTimeRange}
        />
      )}
      {isDateFilter && (
        <DateFilter
          onSave={onChange}
          defaultValue={currentSelectedFilter?.dateRange}
        />
      )}
      {isTimeFilter && (
        <TimeFilter
          onSave={onChange}
          defaultValue={currentSelectedFilter?.timeRange}
        />
      )}
      {isMinMaxFilter && (
        <MinMaxFilter
          type={selectAttributeItem?.data_type}
          onSave={onChange}
          defaultValue={currentSelectedFilter?.minMaxRange}
        />
      )}
    </div>
  );
};
